import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api, { BACKEND_URL } from "@/lib/api";
import { IMG } from "@/lib/assets";
import { PageBanner } from "@/components/ui/PageBanner";
import { CalmRecovery } from "@/components/ui/CalmRecovery";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Clock, Waves, Wind, Play, Pause, X } from "lucide-react";

const CATS = [
  { key: "all", label: "All" },
  { key: "yoga", label: "Yoga" },
  { key: "meditation", label: "Meditation" },
];

export default function MindFlow() {
  const [cat, setCat] = useState("all");
  const [active, setActive] = useState(null);
  const { data: programs = [] } = useQuery({
    queryKey: ["mind", cat],
    queryFn: async () => (await api.get("/api/mind/programs", { params: cat !== "all" ? { category: cat } : {} })).data,
  });

  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.yoga} overline="mind & flow" title="Mind & Flow"
        subtitle="Calm and recovery sessions with your OCTON coach, plus guided yoga, breathwork and meditation programmes." />

      <CalmRecovery />

      <div className="pt-4">
        <span className="mono-label text-acid">programmes</span>
        <h2 className="font-heading font-black text-2xl tracking-tight mt-1">Yoga, breathwork and meditation</h2>
      </div>

      <div className="flex gap-2">
        {CATS.map((c) => (
          <button key={c.key} onClick={() => setCat(c.key)} data-testid={`mind-cat-${c.key}`}
            className={`px-4 h-9 rounded-full text-sm border transition-colors ${
              cat === c.key ? "border-acid text-acid bg-acid/10" : "border-[#1E222A] text-[#8A91A6] hover:border-[#343A4A]"}`}>
            {c.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {programs.map((p, i) => (
          <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="group surface rounded-xl overflow-hidden" data-testid={`mind-card-${i}`}>
            <div className="relative h-48 overflow-hidden">
              <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(0deg,#0F1115 8%,transparent 70%)" }} />
              <span className="absolute top-3 left-3 mono-label px-2.5 py-1 rounded-full bg-black/60 border border-white/10 flex items-center gap-1 text-acid">
                {p.category === "yoga" ? <Waves className="w-3 h-3" /> : <Wind className="w-3 h-3" />} {p.category}
              </span>
              <button onClick={() => setActive(p)} data-testid={`mind-play-${i}`}
                className="absolute bottom-3 right-3 w-11 h-11 rounded-full bg-acid text-black flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#AACC00]">
                <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
              </button>
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="mono-label text-bayes">{p.level}</span>
                <span className="mono-label flex items-center gap-1"><Clock className="w-3 h-3" />{p.duration_min} min</span>
              </div>
              <h3 className="font-heading font-bold text-lg tracking-tight">{p.title}</h3>
              <p className="text-[#8A91A6] text-sm mt-1">{p.description}</p>
              <p className="mono-label text-acid mt-3">{p.focus}</p>
            </div>
          </motion.div>
        ))}
        {programs.length === 0 && <p className="text-[#565C6F] text-sm">No programmes found.</p>}
      </div>

      <GuidedSession program={active} onClose={() => setActive(null)} />
    </div>
  );
}

function GuidedSession({ program, onClose }) {
  const [phase, setPhase] = useState(0);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const open = !!program;
  const phases = program ? [
    { t: "Center & Breathe", d: `Settle in for ${program.title}. Soften your shoulders and take three slow breaths with me.` },
    { t: "Warm & Open", d: `We begin gently, focusing on ${program.focus.toLowerCase()}. Move with your breath, no strain.` },
    { t: "The Flow", d: program.description },
    { t: "Deepen", d: "Sink a little further into each hold. Notice where you can release tension and let it go." },
    { t: "Rest & Integrate", d: "Come to stillness. Let the work settle. Carry this calm, focused energy with you." },
  ] : [];

  const speak = async (text) => {
    try {
      audioRef.current?.pause();
      const res = await fetch(`${BACKEND_URL}/api/ava/narrate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error();
      audioRef.current = new Audio(URL.createObjectURL(await res.blob()));
      audioRef.current.play();
    } catch (_) {
      if ("speechSynthesis" in window) window.speechSynthesis.speak(new SpeechSynthesisUtterance(text));
    }
  };

  const startPhase = (i) => { setPhase(i); setPlaying(true); speak(phases[i].d); };
  useEffect(() => { if (open) { setPhase(0); setPlaying(false); } return () => audioRef.current?.pause(); }, [open]);

  if (!program) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { audioRef.current?.pause(); onClose(); } }}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden border-[#1E222A] bg-black" data-testid="guided-session">
        <DialogHeader className="sr-only"><DialogTitle>{program.title}</DialogTitle><DialogDescription>Guided session</DialogDescription></DialogHeader>
        <div className="relative aspect-video overflow-hidden">
          <motion.img src={program.image} alt="" className="absolute inset-0 w-full h-full object-cover"
            animate={{ scale: playing ? [1, 1.12] : 1 }} transition={{ duration: 20, ease: "linear", repeat: Infinity, repeatType: "reverse" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(0deg,#000 8%,transparent 60%)" }} />
          <div className="absolute top-3 left-4"><span className="mono-label text-acid">{program.category} · {program.duration_min} min</span>
            <h3 className="font-heading font-black text-2xl tracking-tight">{program.title}</h3></div>
          <div className="absolute bottom-4 left-4 right-4">
            <div className="glass rounded-lg p-4 mb-3">
              <span className="mono-label text-acid">Phase {phase + 1} · {phases[phase].t}</span>
              <p className="text-white text-sm md:text-base mt-1" data-testid="guided-caption">{phases[phase].d}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => startPhase(phase)} data-testid="guided-play"
                className="w-12 h-12 rounded-full bg-acid text-black flex items-center justify-center hover:bg-[#AACC00]">
                {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" />}
              </button>
              <div className="flex-1 flex gap-1">
                {phases.map((ph, i) => (
                  <button key={ph.t} onClick={() => startPhase(i)} data-testid={`guided-phase-${i}`}
                    className={`flex-1 h-1.5 rounded-full transition-colors ${i <= phase ? "bg-acid" : "bg-white/20"}`} />
                ))}
              </div>
              {phase < phases.length - 1 && (
                <button onClick={() => startPhase(phase + 1)} className="mono-label text-white hover:text-acid px-2" data-testid="guided-next">Next ›</button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
